/**
 * LocationPicker Component
 * Interactive map for selecting branch location with draggable marker
 */
import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import { MapPin, Navigation, Search, Loader2 } from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Custom marker icon
const customIcon = new L.Icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Component to handle map clicks
const MapClickHandler = ({ onLocationSelect }) => {
  useMapEvents({
    click: (e) => {
      onLocationSelect(e.latlng.lat, e.latlng.lng);
    }
  });
  return null;
};

// Component to update map center
const MapCenterUpdater = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, map.getZoom());
    }
  }, [center, map]);
  return null;
};

// Draggable marker component
const DraggableMarker = ({ position, onDragEnd }) => {
  const markerRef = useRef(null);
  
  const eventHandlers = {
    dragend() {
      const marker = markerRef.current;
      if (marker != null) {
        const latlng = marker.getLatLng();
        onDragEnd(latlng.lat, latlng.lng);
      }
    },
  };

  return position ? (
    <Marker
      draggable={true}
      eventHandlers={eventHandlers}
      position={position}
      ref={markerRef}
      icon={customIcon}
    />
  ) : null;
};

const LocationPicker = ({ 
  lat, 
  lng, 
  onLocationChange,
  address = ''
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [mapCenter, setMapCenter] = useState(null);
  
  // Default to Mexico City if no coordinates
  const defaultCenter = [19.4326, -99.1332];
  
  const position = (lat && lng) ? [parseFloat(lat), parseFloat(lng)] : null;
  const center = mapCenter || position || defaultCenter;

  // Search for address using Nominatim (OpenStreetMap)
  const searchAddress = async () => {
    if (!searchQuery.trim()) return;
    
    setSearching(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&countrycodes=mx&limit=1`
      );
      const data = await response.json();
      
      if (data && data.length > 0) {
        const result = data[0];
        const newLat = parseFloat(result.lat);
        const newLng = parseFloat(result.lon);
        onLocationChange(newLat, newLng);
        setMapCenter([newLat, newLng]);
      } else {
        alert('No se encontró la dirección. Intenta ser más específico o coloca el marcador manualmente.');
      }
    } catch (error) {
      console.error('Error searching address:', error);
      alert('Error al buscar dirección');
    } finally {
      setSearching(false);
    }
  };

  // Get current location
  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert('Tu navegador no soporta geolocalización');
      return;
    }
    
    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const newLat = position.coords.latitude;
        const newLng = position.coords.longitude;
        onLocationChange(newLat, newLng);
        setMapCenter([newLat, newLng]);
        setGettingLocation(false);
      },
      (error) => {
        console.error('Error getting location:', error);
        alert('No se pudo obtener tu ubicación');
        setGettingLocation(false);
      },
      { enableHighAccuracy: true }
    );
  };

  // Handle map click
  const handleLocationSelect = (newLat, newLng) => {
    onLocationChange(newLat, newLng);
  };

  // Handle marker drag
  const handleMarkerDrag = (newLat, newLng) => {
    onLocationChange(newLat, newLng);
  };

  // Search when pressing Enter
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      searchAddress();
    }
  };

  // Use address as initial search if no coordinates
  useEffect(() => {
    if (address && !lat && !lng) {
      setSearchQuery(address);
    }
  }, [address, lat, lng]);

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-slate-700">
        <MapPin className="w-4 h-4 inline mr-1" />
        Ubicación en el Mapa
      </label>
      
      <p className="text-xs text-slate-500">
        Busca la dirección o haz clic en el mapa para colocar el marcador. Puedes arrastrar el marcador para ajustar la posición exacta.
      </p>
      
      {/* Search Bar */}
      <div className="flex gap-2">
        <div className="flex-1 relative">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Buscar dirección..."
            className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
          />
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        </div>
        <button
          type="button"
          onClick={searchAddress}
          disabled={searching || !searchQuery.trim()}
          className="px-3 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 disabled:opacity-50 text-sm"
        >
          {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Buscar'}
        </button>
        <button
          type="button"
          onClick={getCurrentLocation}
          disabled={gettingLocation}
          className="px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 disabled:opacity-50"
          title="Usar mi ubicación"
        >
          {gettingLocation ? <Loader2 className="w-4 h-4 animate-spin" /> : <Navigation className="w-4 h-4" />}
        </button>
      </div>

      {/* Map */}
      <div className="h-64 rounded-lg overflow-hidden border border-slate-200 relative z-0">
        <MapContainer
          center={center}
          zoom={15}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapClickHandler onLocationSelect={handleLocationSelect} />
          <MapCenterUpdater center={mapCenter} />
          <DraggableMarker position={position} onDragEnd={handleMarkerDrag} />
        </MapContainer>
      </div>

      {/* Coordinates Display */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Latitud</label>
          <input
            type="text"
            readOnly
            value={lat || ''}
            className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-600"
            placeholder="Sin definir"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Longitud</label>
          <input
            type="text"
            readOnly
            value={lng || ''}
            className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-600"
            placeholder="Sin definir"
          />
        </div>
      </div>

      {position && (
        <p className="text-xs text-green-600 flex items-center gap-1">
          <MapPin className="w-3 h-3" />
          Ubicación guardada correctamente
        </p>
      )}
    </div>
  );
};

export default LocationPicker;
